/**
 * In-race HUD.
 *
 * Drawn in screen space at native resolution so the numbers stay legible, but
 * styled with flat blocks and a monospace face to match the pixel-art world.
 */

import { clamp, formatMoney, formatTime } from '@shared/math.js';
import { PALETTE, type Ui } from './ui.js';
import type { HudState, RaceSession } from '../race/race-session.js';
import type { Camera } from '../core/camera.js';
import type { Renderer } from '../render/renderer.js';

export interface HudDrawOptions {
  paused: boolean;
  showRacingLine: boolean;
  touchEnabled: boolean;
  showMinimap: boolean;
  fps: number;
  ping: number;
  cameraMode: number;
}

const PANEL_ALPHA = 'rgba(13,15,20,0.78)';

export function drawHud(
  ui: Ui,
  renderer: Renderer,
  session: RaceSession,
  hud: HudState,
  camera: Camera,
  options: HudDrawOptions,
  dt: number,
): void {
  const ctx = ui.ctx;
  const w = ui.width;
  const h = ui.height;
  const s = ui.scale;
  const margin = Math.round(14 * s);
  const speed = session.smoothSpeed(hud.speedKmh, dt);

  // ------------------------------------------------------------- speed pod
  const podW = Math.round(190 * s);
  const podH = Math.round(96 * s);
  const podX = w - podW - margin;
  const podY = h - podH - margin;
  ctx.fillStyle = PANEL_ALPHA;
  ctx.fillRect(podX, podY, podW, podH);
  ctx.strokeStyle = PALETTE.line;
  ctx.lineWidth = 1;
  ctx.strokeRect(podX + 0.5, podY + 0.5, podW - 1, podH - 1);

  ui.text(String(Math.round(speed)), podX + 14 * s, podY + 40 * s, { size: 40, weight: '700' });
  ui.text('km/h', podX + 14 * s + ui.measure(String(Math.round(speed)), 40, '700') + 8 * s, podY + 52 * s, {
    size: 12,
    colour: PALETTE.muted,
  });

  // Rev bar: fills with engine speed and flashes near the limiter.
  const rpm01 = clamp(hud.rpm / 7600, 0, 1);
  const barW = podW - 28 * s;
  const barY = podY + 66 * s;
  ui.bar({ x: podX + 14 * s, y: barY, w: barW, h: 10 * s }, rpm01, rpm01 > 0.92 ? PALETTE.bad : rpm01 > 0.75 ? PALETTE.accent : PALETTE.good);

  const gearW = 46 * s;
  ui.text(`N${hud.gear}`, podX + podW - gearW - 4 * s, podY + 40 * s, {
    size: 26,
    weight: '700',
    colour: PALETTE.accent,
    align: 'right',
  });

  // Turbo charge.
  const turboY = podY + 80 * s;
  ui.bar({ x: podX + 14 * s, y: turboY, w: barW, h: 5 * s }, hud.turboCharge, '#4fd1ff');

  // --------------------------------------------------------------- top bar
  const topH = Math.round(38 * s);
  ctx.fillStyle = PANEL_ALPHA;
  ctx.fillRect(margin, margin, w - margin * 2, topH);
  ctx.strokeStyle = PALETTE.line;
  ctx.strokeRect(margin + 0.5, margin + 0.5, w - margin * 2 - 1, topH - 1);

  const entries: Array<[string, string]> = [
    ['POS', `${hud.position}/${hud.racerCount}`],
    // Checkpoint Sprint is decided on gates, not laps.
    hud.mode === 'checkpoint'
      ? ['GATES', `${hud.checkpoint}/${hud.checkpointTarget}`]
      : ['LAP', `${hud.lap}/${hud.totalLaps}`],
    ['TIME', formatTime(hud.lapTime)],
    ['BEST', hud.bestLap > 0 ? formatTime(hud.bestLap) : '--:--.---'],
    ['RACE', formatTime(hud.raceTime)],
  ];

  const slotW = (w - margin * 2) / entries.length;
  entries.forEach(([label, value], i) => {
    const cx = margin + slotW * i + slotW / 2;
    ui.text(label, cx, margin + 12 * s, { size: 10, colour: PALETTE.muted, align: 'center' });
    ui.text(value, cx, margin + 26 * s, { size: 15, weight: '700', align: 'center' });
    if (i > 0) {
      ctx.strokeStyle = PALETTE.line;
      ctx.beginPath();
      ctx.moveTo(Math.round(margin + slotW * i) + 0.5, margin + 6 * s);
      ctx.lineTo(Math.round(margin + slotW * i) + 0.5, margin + topH - 6 * s);
      ctx.stroke();
    }
  });

  // ------------------------------------------------------------- drift meter
  if (hud.driftPending > 1 || hud.driftScore > 0) {
    const driftY = margin + topH + 10 * s;
    const drifting = hud.driftPending > 1;
    ui.text(`DRIFT ${Math.round(hud.driftScore)}`, margin + 4 * s, driftY + 10 * s, {
      size: 13,
      weight: '700',
      colour: drifting ? PALETTE.accent : PALETTE.muted,
    });
    if (drifting) {
      ui.text(`x${hud.driftCombo.toFixed(1)}`, margin + 150 * s, driftY + 10 * s, { size: 13, colour: PALETTE.good });
    }
  }

  // -------------------------------------------------------------- standings
  if (hud.standings.length > 1) {
    const rowH = Math.round(20 * s);
    const listW = Math.round(210 * s);
    const listH = Math.min(rowH * (hud.standings.length + 1), h * 0.42);
    const listX = margin;
    const listY = h - listH - margin;
    ctx.fillStyle = PANEL_ALPHA;
    ctx.fillRect(listX, listY, listW, listH);
    ctx.strokeStyle = PALETTE.line;
    ctx.strokeRect(listX + 0.5, listY + 0.5, listW - 1, listH - 1);
    ui.text('STANDINGS', listX + 8 * s, listY + rowH / 2, { size: 10, colour: PALETTE.muted });
    hud.standings.slice(0, 10).forEach((row, i) => {
      const y = listY + rowH * (i + 1) + rowH / 2;
      if (y > listY + listH - rowH / 2) return;
      if (row.isPlayer) {
        ctx.fillStyle = 'rgba(242,193,78,0.14)';
        ctx.fillRect(listX + 1, y - rowH / 2, listW - 2, rowH);
      }
      ui.text(`${row.position}`, listX + 10 * s, y, { size: 12, weight: '700', colour: row.isPlayer ? PALETTE.accent : PALETTE.ink });
      ctx.fillStyle = row.color;
      ctx.fillRect(listX + 26 * s, y - 4 * s, 8 * s, 8 * s);
      const name = row.isPlayer ? 'YOU' : row.name.split('·')[0]!.trim().slice(0, 14);
      ui.text(name, listX + 40 * s, y, {
        size: 12,
        colour: row.eliminated ? PALETTE.bad : PALETTE.ink,
        maxWidth: listW - 90 * s,
      });
      ui.text(`L${row.lap}`, listX + listW - 10 * s, y, { size: 11, colour: PALETTE.muted, align: 'right' });
    });
  }

  // ---------------------------------------------------------------- minimap
  if (options.showMinimap) {
    drawMinimap(ui, renderer, session, camera, margin, margin + topH + (hud.driftPending > 1 ? 34 * s : 10 * s));
  }

  // ----------------------------------------------------------- touch controls
  if (options.touchEnabled) drawTouchControls(ui);

  // -------------------------------------------------------------- warnings
  if (hud.wrongWay) {
    const blink = Math.sin(performance.now() / 160) > 0;
    if (blink) {
      ui.text('WRONG WAY', w / 2, h * 0.28, { size: Math.round(26 * s), weight: '700', colour: PALETTE.bad, align: 'center' });
    }
  }
  if (hud.offTrack && !hud.finished) {
    ui.text(hud.surface.toUpperCase(), w / 2, h * 0.2, {
      size: Math.round(13 * s),
      colour: PALETTE.warn,
      align: 'center',
    });
  }

  // ----------------------------------------------------------- notifications
  hudNotifications(ui, session, h, s);

  // -------------------------------------------------------------- countdown
  if (hud.countdown > 0) {
    const value = Math.ceil(hud.countdown);
    const text = value <= 1 ? 'GO!' : String(value);
    const progress = 1 - (hud.countdown - Math.floor(hud.countdown));
    const size = Math.round((64 + (1 - progress) * 20) * s);
    ui.text(text, w / 2, h * 0.44, {
      size,
      weight: '700',
      colour: value <= 1 ? PALETTE.good : PALETTE.accent,
      align: 'center',
    });
  }

  if (hud.eliminated) {
    drawFinishBanner(ui, session, hud, false);
  }

  if (hud.finished) {
    drawFinishBanner(ui, session, hud, true);
  }

  // ------------------------------------------------------------- debug line
  if (options.fps > 0) {
    ui.text(`${options.fps} fps${options.ping > 0 ? ` · ${options.ping}ms` : ''}`, w - margin - 4 * s, margin + topH + 12 * s, {
      size: 10,
      colour: PALETTE.muted,
      align: 'right',
    });
  }

  if (options.showRacingLine && session.nextCheckpoint()) {
    const gate = session.nextCheckpoint()!;
    const p = renderer.worldToScreen(gate.x, gate.y, camera);
    ctx.strokeStyle = 'rgba(242,193,78,0.8)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 16 * s, 0, Math.PI * 2);
    ctx.stroke();
  }
}

/**
 * The end-of-race popup.
 *
 * Crossing the line used to be silent until the whole field had finished, which
 * left the player staring at a frozen car. Now the moment your race ends you get
 * the result, the provisional payout and a note that the full breakdown is on
 * its way.
 */
function drawFinishBanner(ui: Ui, session: RaceSession, hud: HudState, finished: boolean): void {
  const s = ui.scale;
  const w = Math.min(420 * s, ui.width - 40);
  const h = 156 * s;
  const x = (ui.width - w) / 2;
  const y = ui.height * 0.28;

  ui.rect({ x, y, w, h }, 'rgba(13,15,20,0.9)', finished ? PALETTE.good : PALETTE.bad);
  ui.rect({ x, y, w, h: 4 }, finished ? PALETTE.good : PALETTE.bad);

  ui.text(finished ? 'FINISHED' : 'ELIMINATED', x + w / 2, y + 34 * s, {
    size: Math.round(28 * s),
    weight: '700',
    align: 'center',
    colour: finished ? PALETTE.good : PALETTE.bad,
  });

  const place = hud.position;
  const suffix = place % 10 === 1 && place !== 11 ? 'st' : place % 10 === 2 && place !== 12 ? 'nd' : place % 10 === 3 && place !== 13 ? 'rd' : 'th';
  ui.text(`${place}${suffix} of ${hud.racerCount}`, x + w / 2, y + 70 * s, {
    size: Math.round(18 * s),
    weight: '700',
    align: 'center',
  });

  const rewards = session.outcome?.rewards;
  if (rewards) {
    ui.text(`+${formatMoney(rewards.money)}`, x + w / 2, y + 102 * s, {
      size: Math.round(22 * s),
      weight: '700',
      align: 'center',
      colour: PALETTE.accent,
    });
  }

  ui.text('Full results in a moment…', x + w / 2, y + 132 * s, {
    size: Math.round(11 * s),
    align: 'center',
    colour: PALETTE.muted,
  });
}

function hudNotifications(ui: Ui, session: RaceSession, height: number, s: number): void {  const now = Date.now();
  const visible = session.notifications.filter((n) => now - n.at < 3200);
  visible.forEach((note, i) => {
    const age = (now - note.at) / 3200;
    ui.ctx.globalAlpha = clamp(1 - Math.max(0, age - 0.7) / 0.3, 0, 1);
    ui.text(note.text, ui.width / 2, height * 0.62 + i * 24 * s, {
      size: Math.round(16 * s),
      weight: '700',
      align: 'center',
      colour: note.kind === 'lap' ? PALETTE.accent : note.kind === 'crash' ? PALETTE.bad : note.kind === 'achievement' ? PALETTE.good : PALETTE.ink,
    });
    ui.ctx.globalAlpha = 1;
  });
}

function drawMinimap(ui: Ui, renderer: Renderer, session: RaceSession, camera: Camera, x: number, y: number): void {
  const size = Math.round(150 * ui.scale);
  const ctx = ui.ctx;
  const track = session.track;
  ctx.fillStyle = PANEL_ALPHA;
  ctx.fillRect(x, y, size, size);
  ctx.strokeStyle = PALETTE.line;
  ctx.strokeRect(x + 0.5, y + 0.5, size - 1, size - 1);

  const pad = 8 * ui.scale;
  const scaleX = (size - pad * 2) / track.def.width;
  const scaleY = (size - pad * 2) / track.def.height;
  const scale = Math.min(scaleX, scaleY);
  const offsetX = x + pad + ((size - pad * 2) - track.def.width * scale) / 2;
  const offsetY = y + pad + ((size - pad * 2) - track.def.height * scale) / 2;

  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, size, size);
  ctx.clip();
  // Track ribbon from the centreline: cheap and instantly readable.
  ctx.strokeStyle = 'rgba(190,200,215,0.5)';
  ctx.lineWidth = Math.max(2, 10 * scale * ui.scale);
  ctx.lineJoin = 'round';
  ctx.beginPath();
  track.path.forEach((p, i) => {
    const px = offsetX + p.x * scale;
    const py = offsetY + p.y * scale;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.stroke();

  for (const dot of session.minimapDots()) {
    ctx.fillStyle = dot.isPlayer ? '#f2c14e' : dot.color;
    const px = offsetX + dot.x * scale;
    const py = offsetY + dot.y * scale;
    const r = dot.isPlayer ? 3.2 * ui.scale : 2.4 * ui.scale;
    ctx.beginPath();
    ctx.arc(px, py, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  void renderer;
  void camera;
}

function drawTouchControls(ui: Ui): void {
  // The touch layout is handled by the game (which owns the input manager), so
  // the HUD only draws the affordances here.
  const ctx = ui.ctx;
  const s = ui.scale;
  const w = ui.width;
  const h = ui.height;
  const radius = 64 * s;
  const cx = 92 * s;
  const cy = h - 110 * s;
  ctx.strokeStyle = 'rgba(234,232,224,0.28)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, 10 * s, 0, Math.PI * 2);
  ctx.stroke();

  const btns: Array<[string, number, number]> = [
    ['GO', w - 210 * s, h - 150 * s],
    ['BRAKE', w - 150 * s, h - 74 * s],
    ['SLIDE', w - 268 * s, h - 74 * s],
  ];
  for (const [label, bx, by] of btns) {
    ctx.beginPath();
    ctx.arc(bx, by, 34 * s, 0, Math.PI * 2);
    ctx.stroke();
    ui.text(label, bx, by, { size: 11, align: 'center', colour: PALETTE.muted });
  }
}
